import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { formatApiError } from '../lib/api';
import { Eye, EyeSlash } from '@phosphor-icons/react';

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', password: '', confirmPassword: '' });
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }
    if (form.password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caracteres.');
      return;
    }
    setLoading(true);
    try {
      const { confirmPassword, ...data } = form;
      await register(data);
      navigate('/espace-membre/dashboard');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
    setLoading(false);
  };

  const update = (field, value) => setForm(p => ({ ...p, [field]: value }));

  return (
    <div className="pt-20 min-h-screen flex items-center justify-center py-12" data-testid="register-page">
      <div className="w-full max-w-md px-6">
        <div className="text-center mb-10">
          <p className="text-xs uppercase tracking-[0.2em] text-desti-gold mb-3">Rejoignez-nous</p>
          <h1 className="font-heading text-3xl sm:text-4xl text-white">Creer un compte</h1>
        </div>
        <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-desti-gold mb-2 block">Prenom</label>
              <input type="text" required value={form.firstName} onChange={e => update('firstName', e.target.value)}
                className="w-full bg-desti-black border-b border-white/20 px-3 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-desti-gold transition-colors"
                placeholder="Prenom" data-testid="register-firstname-input" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-desti-gold mb-2 block">Nom</label>
              <input type="text" required value={form.lastName} onChange={e => update('lastName', e.target.value)}
                className="w-full bg-desti-black border-b border-white/20 px-3 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-desti-gold transition-colors"
                placeholder="Nom" data-testid="register-lastname-input" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-desti-gold mb-2 block">Email</label>
            <input type="email" required value={form.email} onChange={e => update('email', e.target.value)}
              className="w-full bg-desti-black border-b border-white/20 px-3 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-desti-gold transition-colors"
              placeholder="votre@email.fr" data-testid="register-email-input" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-desti-gold mb-2 block">Telephone</label>
            <input type="tel" value={form.phone} onChange={e => update('phone', e.target.value)}
              className="w-full bg-desti-black border-b border-white/20 px-3 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-desti-gold transition-colors"
              placeholder="06 XX XX XX XX" data-testid="register-phone-input" />
          </div>
          <div className="relative">
            <label className="text-xs uppercase tracking-widest text-desti-gold mb-2 block">Mot de passe</label>
            <input type={showPw ? 'text' : 'password'} required value={form.password} onChange={e => update('password', e.target.value)}
              className="w-full bg-desti-black border-b border-white/20 px-3 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-desti-gold transition-colors pr-10"
              placeholder="Min. 6 caracteres" data-testid="register-password-input" />
            <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-2 bottom-3 text-white/40 hover:text-white/60">
              {showPw ? <EyeSlash size={18} /> : <Eye size={18} />}
            </button>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-desti-gold mb-2 block">Confirmer</label>
            <input type="password" required value={form.confirmPassword} onChange={e => update('confirmPassword', e.target.value)}
              className="w-full bg-desti-black border-b border-white/20 px-3 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-desti-gold transition-colors"
              placeholder="Confirmez le mot de passe" data-testid="register-confirm-input" />
          </div>
          {error && <p className="text-red-400 text-sm" data-testid="register-error">{error}</p>}
          <button type="submit" disabled={loading}
            className="w-full bg-desti-gold text-desti-black font-medium py-4 tracking-wide hover:bg-desti-gold-hover transition-colors disabled:opacity-50"
            data-testid="register-submit-btn">
            {loading ? 'Creation...' : 'Creer mon compte'}
          </button>
        </form>
        <p className="text-center text-sm text-desti-text mt-8">
          Deja inscrit ? <Link to="/connexion" className="text-desti-gold hover:text-desti-gold-hover transition-colors" data-testid="login-link-from-register">Se connecter</Link>
        </p>
      </div>
    </div>
  );
}
